/**
 * App state.
 *
 * Much smaller than Cloud's, and the reason is the whole difference between the
 * two products. Cloud has a vault: it carries `locked` and `unlocked` states,
 * an idle timer, and keys in memory. Review holds courses in plaintext, so there
 * is nothing to unlock and no key to lose — the only question is whether the
 * caller is signed in.
 *
 * `unreachable` is kept, and is not the same as `signedOut`. Cloud learned that
 * one the hard way: collapsing a 500 or a dropped connection into "signed out"
 * bounces a signed-in user to the sign-in page for a network blip, which then
 * looks like the session broke.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore
} from 'react';
import type React from 'react';
import {
  applyBranding,
  applyCachedBranding,
  getBranding,
  setProduct,
  subscribeBranding
} from '@shared/apply-branding';
import { startPresence, stopPresence } from '@shared/presence-client';
import type { Branding } from '@shared/branding';

export type AppStatus =
  | 'loading'
  | 'signedOut'
  | 'secondFactor'
  /** The deployment is incomplete — a missing binding, an unmigrated database.
   *  Separated from `unreachable` because the two need completely different
   *  words: one is "wait a moment", the other is "finish the deploy". */
  | 'notReady'
  | 'unreachable'
  | 'ready';

/** Whatever the server could tell us about why it is not ready. */
export interface NotReady {
  message: string;
  missingTables?: string[];
  missingColumns?: string[];
  storage?: boolean;
  hint?: string;
}

export interface Me {
  id: string;
  name: string;
  email: string;
  plan: string;
  /** Projects this plan may keep open at once. -1 is unlimited, 0 is no Review. */
  projectsAllowed: number;
  projectsOpen: number;
  /** What this account's organisation shows instead of Mauvely, or null —
   *  which is almost every account. */
  branding: Branding | null;
}

interface AppValue {
  status: AppStatus;
  me: Me | null;
  notReady: NotReady | null;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AppValue | null>(null);

export function useApp(): AppValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp outside AppProvider');
  return v;
}

/**
 * The organisation's branding from the last load, before anything renders.
 *
 * Module scope on purpose: a branded site otherwise paints Mauvely violet under
 * the word `review.`, asks who the viewer is, and repaints. The server's answer
 * replaces this the moment it arrives, so a stale value survives exactly one
 * paint.
 */
setProduct({ name: 'Mauvely Review', short: 'Review' });
applyCachedBranding();

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AppStatus>('loading');
  const [me, setMe] = useState<Me | null>(null);
  const [notReady, setNotReady] = useState<NotReady | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/review/me', { headers: { accept: 'application/json' } });
      if (res.status === 401) {
        setMe(null);
        // A signed-out viewer is nobody's member. Keeping the colour would
        // leave a customer's branding on a screen that belongs to Mauvely, and
        // on a shared machine it says which company the last person worked for.
        applyBranding(null);
        setStatus('signedOut');
        return;
      }
      if (res.status === 403) {
        // Authenticated but has not cleared the second factor. Its own state,
        // because sending this to /signin mints another session in the same
        // condition and the user loops forever.
        setMe(null);
        // Cleared, like the 401 above: nobody is established as a member yet.
        // The outage branches below deliberately do not clear — that would
        // empty the cache and repaint a customer's product as Mauvely for the
        // length of the outage.
        applyBranding(null);
        setStatus('secondFactor');
        return;
      }
      if (!res.ok) {
        // Find out *why* rather than shrugging. This branch used to render
        // "Review is not answering", which is what a 404 from a route that had
        // never been written looked like — a sentence that sends someone to
        // check their DNS when the answer was in the route table.
        setNotReady(await diagnose(res));
        setStatus('notReady');
        return;
      }
      const next = (await res.json()) as Me;
      setMe(next);
      // Here rather than in an effect: this is where the server's answer
      // arrives, and an effect would repaint a frame later.
      applyBranding(next.branding);
      setNotReady(null);
      setStatus('ready');
    } catch {
      // A genuinely failed fetch — no response at all. This is the only case
      // that is really a network problem.
      setStatus('unreachable');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * Account presence: one socket for the page, and only while somebody is
   * signed in.
   *
   * Keyed on the id rather than on `me` — `refresh()` hands back a fresh object
   * every time it runs, and a socket that reconnected on each of those would be
   * a reconnect per poll.
   *
   * **`me` is the gate, not the route, and that is what keeps this off the
   * reviewer surface.** `AppProvider` wraps `/r/*` too (App.tsx), but a
   * reviewer arrived by link and has no Mauvely account, so `/api/review/me`
   * answers 401, `me` is null and nothing opens. An author who happens to open
   * a share link is signed in and keeps their own status, which is the right
   * answer — presence is about the account, not the screen.
   *
   * `stopPresence` on the way out is not tidiness. The socket is what says
   * somebody is here, and one that outlived the session would keep saying it
   * about whoever signs in next at this browser.
   *
   * Not `api.presence(projectId)`, which is the anonymous reviewer's
   * per-project heartbeat behind the LIVE pill. See components/Presence.tsx.
   */
  useEffect(() => {
    if (!me?.id) {
      stopPresence();
      return;
    }
    startPresence({ app: 'review', path: '/api/review/presence' });
    return () => stopPresence();
  }, [me?.id]);

  return <Ctx.Provider value={{ status, me, notReady, refresh }}>{children}</Ctx.Provider>;
}

/**
 * Turn a failed response into something a person can act on.
 *
 * The health endpoint already knows how to say "these tables are missing" and
 * "there is no R2 binding", so ask it rather than inventing a second vocabulary.
 */
async function diagnose(res: Response): Promise<NotReady> {
  // The 503 from /me already carries the health payload inline.
  try {
    const body = (await res.clone().json()) as {
      error?: NotReady & { code?: string };
    };
    if (body.error) return body.error;
  } catch {
    // Not JSON — a 404 from the asset binding, most likely.
  }

  if (res.status === 404) {
    return {
      message:
        'The Worker is serving the app but not its API. The deployed build is ' +
        'older than this page, or wrangler.jsonc is missing run_worker_first for /api/*.',
      hint: 'Redeploy with `npm run deploy`.'
    };
  }

  try {
    const health = await fetch('/api/review/health', {
      headers: { accept: 'application/json' }
    });
    const body = (await health.json()) as NotReady & { database?: string };
    return {
      ...body,
      message:
        body.database === 'unreachable'
          ? 'The D1 binding is not answering. Check `database_id` in wrangler.jsonc.'
          : 'Review is deployed but not finished setting up.'
    };
  } catch {
    return { message: `The API answered ${res.status} and the health check did not answer.` };
  }
}

/**
 * The organisation's branding, or null for Mauvely.
 *
 * Deliberately **not** on `AppContext`. The reviewer pages render for somebody
 * who arrived by link and has no account at all — often the customer's own
 * customer — and they draw the lockup like every other page. A context hook
 * would throw there, and threading `branding` down as a prop would put it on
 * all ten call sites in this repo.
 *
 * So it reads the same module-level store `applyBranding` writes, which is the
 * accent's model exactly: a global the chrome reads, set in one place.
 */
export function useBranding(): Branding | null {
  return useSyncExternalStore(subscribeBranding, getBranding, getBranding);
}
